<template>
  <div class="p-0">
    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
    </div>

    <div v-else>
      <div class="mb-5">
        <router-link to="/leave/requests" class="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900">
          <i class="fa-solid fa-arrow-left mr-2"></i>
          Volver a mis solicitudes
        </router-link>
      </div>

      <header class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
        <div>
          <div class="flex flex-wrap items-center gap-2">
            <h1 class="text-2xl font-bold text-gray-900">{{ requestTitle(request) }}</h1>
            <span class="px-2 py-1 text-xs font-semibold rounded-full border" :class="statusClass(request.status)">
              {{ statusLabel(request.status) }}
            </span>
          </div>
          <p class="mt-1 text-sm text-gray-500">
            Solicitud {{ request.id || request.request_id || '-' }}
            <span v-if="request.created_at"> · Presentada el {{ formatDateTime(request.created_at) }}</span>
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            v-if="canEditRequest(request)"
            @click="editRequest"
            class="px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800"
          >
            {{ request.status === 'draft' ? 'Continuar' : 'Corregir solicitud' }}
          </button>
          <button
            v-if="canSubmitFromDetail"
            @click="submitRequest"
            :disabled="saving"
            class="px-4 py-2 text-sm font-semibold bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:opacity-50"
          >
            Enviar
          </button>
          <button
            v-if="canCancelRequest(request)"
            @click="showCancelModal = true"
            class="px-4 py-2 text-sm font-semibold bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
        </div>
      </header>

      <div v-if="notice" class="mb-5 bg-orange-50 border border-orange-100 text-orange-800 rounded-lg px-4 py-3">
        <i class="fa-solid fa-triangle-exclamation mr-2"></i>
        <strong>Acción requerida:</strong> {{ notice }}
      </div>

      <div v-if="success" class="mb-5 bg-green-50 border border-green-100 text-green-700 rounded-lg px-4 py-3">
        {{ success }}
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div class="xl:col-span-2 space-y-5">
          <section class="panel">
            <h2 class="panel-title">
              <i class="fa-regular fa-file-lines mr-2"></i>
              Resumen de la Solicitud
            </h2>
            <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div class="info-box">
                <div class="info-label">Tipo de licencia</div>
                <div class="info-value">{{ requestTitle(request) }}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Fecha solicitada</div>
                <div class="info-value">{{ dateRange }}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Duración</div>
                <div class="info-value">{{ durationText }}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Tipo de jornada</div>
                <div class="info-value">{{ request.full_day === false ? 'Horario parcial' : 'Día completo' }}</div>
              </div>
            </div>
            <div class="mt-4 border-t border-gray-100 pt-4">
              <div class="info-label">Motivación / justificación</div>
              <p class="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{{ request.reason || '-' }}</p>
            </div>
          </section>

          <section class="panel">
            <div class="flex items-center justify-between gap-3">
              <h2 class="panel-title">
                <i class="fa-solid fa-paperclip mr-2"></i>
                Documentación Adjunta
              </h2>
              <label
                v-if="canUploadDocument(request)"
                class="inline-flex items-center px-3 py-2 text-sm font-semibold text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 cursor-pointer"
              >
                Subir documento
                <input type="file" class="hidden" @change="uploadDocument" accept=".pdf,.jpg,.jpeg,.png" />
              </label>
            </div>

            <div class="mt-4">
              <label v-if="canUploadDocument(request)" class="label">Tipo de documento</label>
              <select v-if="canUploadDocument(request)" v-model="selectedDocumentType" class="field mb-4">
                <option value="">General</option>
                <option v-for="requirement in documentRequirements" :key="requirement.code || requirement.id" :value="requirement.code">
                  {{ requirement.name || requirement.code }}
                </option>
              </select>

              <div v-if="documents.length === 0" class="border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500">
                No hay documentos adjuntos.
              </div>
              <div v-else class="space-y-3">
                <div
                  v-for="document in documents"
                  :key="document.id || document.document_id || document.file_name"
                  class="flex items-center justify-between gap-3 border border-gray-200 rounded-lg p-3"
                  :class="documentStatusBorder(document)"
                >
                  <div class="min-w-0">
                    <div class="font-semibold text-gray-900 truncate">{{ documentName(document) }}</div>
                    <div class="text-xs text-gray-500 truncate">
                      {{ document.document_type || document.type || 'General' }}
                      <span v-if="document.status"> · {{ document.status }}</span>
                    </div>
                    <div v-if="document.rejection_reason || document.reason" class="mt-1 text-xs text-red-700">
                      Motivo rechazo: {{ document.rejection_reason || document.reason }}
                    </div>
                  </div>
                  <button
                    type="button"
                    @click="openDocumentViewer(document)"
                    class="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    title="Ver documento"
                  >
                    <i class="fa-regular fa-eye"></i>
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section class="panel">
            <h2 class="panel-title">
              <i class="fa-regular fa-circle-check mr-2"></i>
              Requisitos
            </h2>
            <div v-if="requirements.length === 0" class="mt-3 text-sm text-gray-500">
              No hay requisitos informados para esta solicitud.
            </div>
            <div v-else class="mt-3 space-y-2">
              <div
                v-for="requirement in requirements"
                :key="requirement.id || requirement.code"
                class="flex items-center justify-between gap-3 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 text-sm"
              >
                <span>{{ requirement.name || requirement.code || 'Requisito' }}</span>
                <span class="text-xs font-semibold text-gray-600">{{ requirement.status || (requirement.passed ? 'Cumplido' : 'Pendiente') }}</span>
              </div>
            </div>
          </section>
        </div>

        <aside class="space-y-5">
          <section class="panel">
            <h2 class="panel-title">Historial del Trámite</h2>
            <div v-if="history.length === 0" class="mt-3 text-sm text-gray-500">No hay historial disponible.</div>
            <div v-else class="mt-4 space-y-4">
              <div v-for="event in history" :key="event.id || event.created_at || event.status" class="flex gap-3">
                <span class="mt-1 w-3 h-3 rounded-full bg-indigo-800 flex-shrink-0"></span>
                <div>
                  <div class="text-sm font-semibold text-gray-900">{{ historyTitle(event) }}</div>
                  <div class="text-xs text-gray-500">{{ formatDateTime(event.created_at || event.date) }}</div>
                  <p v-if="event.comment || event.observation || event.reason" class="mt-1 text-xs text-gray-600 bg-gray-50 rounded p-2">
                    {{ event.comment || event.observation || event.reason }}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section class="bg-indigo-900 text-indigo-50 rounded-xl p-5 shadow-sm">
            <h2 class="font-semibold">Nota del sistema</h2>
            <p class="mt-2 text-sm text-indigo-100">
              Las solicitudes observadas deben corregirse y reenviarse para que dirección revise los cambios.
            </p>
          </section>
        </aside>
      </div>
    </div>

    <div v-if="showCancelModal" class="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
      <div class="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
        <h2 class="text-lg font-bold text-gray-900">Cancelar solicitud</h2>
        <p class="mt-1 text-sm text-gray-600">Indicá el motivo de cancelación.</p>
        <textarea v-model="cancelReason" rows="4" class="field mt-4" placeholder="Motivo de cancelación *"></textarea>
        <div class="mt-5 flex justify-end gap-2">
          <button @click="showCancelModal = false" class="px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
            Volver
          </button>
          <button
            @click="cancelRequest"
            :disabled="saving || !cancelReason.trim()"
            class="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>

    <LeaveDocumentViewer
      v-if="viewerDocument"
      :document="viewerDocument"
      @close="closeDocumentViewer"
    />
  </div>
</template>

<script>
import LeaveDocumentViewer from '@/components/leave/LeaveDocumentViewer.vue';
import Loading from '@/components/commons/Loading.vue';
import { leaveRequestsApi } from '@/service/leave.service';
import {
  canCancelRequest,
  canEditRequest,
  canUploadDocument,
  formatDate,
  formatDateTime,
  requestDocuments,
  requestHistory,
  requestRequirements,
  requestTitle,
  statusClass,
  statusLabel,
  unwrapData,
  unwrapList,
} from '@/utils/leave';

export default {
  name: 'LeaveRequestDetailView',
  components: { LeaveDocumentViewer, Loading },
  data() {
    return {
      loading: false,
      saving: false,
      error: '',
      success: '',
      request: {},
      requirements: [],
      showCancelModal: false,
      cancelReason: '',
      selectedDocumentType: '',
      viewerDocument: null,
    };
  },
  computed: {
    requestId() {
      return this.$route.params.id;
    },
    documents() {
      return requestDocuments(this.request);
    },
    history() {
      return requestHistory(this.request);
    },
    notice() {
      return this.request.observation || this.request.observation_reason || this.request.last_observation || '';
    },
    dateRange() {
      const start = formatDate(this.request.start_date);
      const end = formatDate(this.request.end_date);
      return start === end ? start : `${start} → ${end}`;
    },
    durationText() {
      const days = this.request.requested_days ?? this.request.days ?? this.request.total_days;
      if (days) return `${days} día${Number(days) === 1 ? '' : 's'}`;
      if (this.request.requested_hours) return `${this.request.requested_hours} horas`;
      return '-';
    },
    canSubmitFromDetail() {
      return ['draft', 'observed'].includes(this.request.status);
    },
    documentRequirements() {
      return this.requirements.filter((requirement) => requirement.requirement_type === 'document');
    },
  },
  created() {
    this.fetchRequest();
  },
  methods: {
    statusClass,
    statusLabel,
    requestTitle,
    formatDateTime,
    canEditRequest,
    canCancelRequest,
    canUploadDocument,
    async fetchRequest() {
      this.loading = true;
      this.error = '';
      try {
        const response = await leaveRequestsApi.get(this.requestId);
        this.request = unwrapData(response);
        this.requirements = requestRequirements(this.request);
        await this.fetchRequirements();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo cargar la solicitud.';
      } finally {
        this.loading = false;
      }
    },
    async fetchRequirements() {
      try {
        const response = await leaveRequestsApi.requirements(this.requestId);
        const requirements = unwrapList(response);
        if (requirements.length) this.requirements = requirements;
      } catch {
        // El detalle ya puede traer requisitos; este endpoint es complementario.
      }
    },
    editRequest() {
      this.$router.push(`/leave/requests/${this.requestId}/edit`);
    },
    async submitRequest() {
      this.saving = true;
      this.error = '';
      this.success = '';
      try {
        await leaveRequestsApi.submit(this.requestId);
        this.success = 'Solicitud enviada a revisión.';
        await this.fetchRequest();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo enviar la solicitud.';
      } finally {
        this.saving = false;
      }
    },
    async cancelRequest() {
      this.saving = true;
      this.error = '';
      try {
        await leaveRequestsApi.cancel(this.requestId, this.cancelReason);
        this.showCancelModal = false;
        this.cancelReason = '';
        this.success = 'Solicitud cancelada.';
        await this.fetchRequest();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo cancelar la solicitud.';
      } finally {
        this.saving = false;
      }
    },
    async uploadDocument(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      this.saving = true;
      this.error = '';
      this.success = '';
      try {
        const response = await leaveRequestsApi.addDocument(this.requestId, file, this.selectedDocumentType);
        const document = unwrapData(response);
        this.request = { ...this.request, documents: [...this.documents, document] };
        this.success = 'Documento cargado.';
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo subir el documento.';
      } finally {
        this.saving = false;
      }
    },
    openDocumentViewer(document) {
      this.viewerDocument = document;
    },
    closeDocumentViewer() {
      this.viewerDocument = null;
    },
    documentName(document) {
      return document.file_name || document.name || document.original_name || 'Documento';
    },
    documentStatusBorder(document) {
      if (document.status === 'rejected') return 'border-red-200 bg-red-50';
      if (document.status === 'approved') return 'border-green-200 bg-green-50';
      return 'border-gray-200 bg-white';
    },
    historyTitle(event) {
      return event.title || event.label || statusLabel(event.status) || 'Movimiento';
    },
  },
};
</script>

<style scoped>
.panel {
  @apply bg-white border border-gray-200 rounded-xl shadow-sm p-5;
}
.panel-title {
  @apply font-bold text-gray-900;
}
.info-box {
  @apply bg-gray-50 border border-gray-100 rounded-md p-3;
}
.info-label {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500;
}
.info-value {
  @apply mt-1 font-semibold text-gray-900;
}
.label {
  @apply block text-xs font-semibold text-gray-500 mb-1;
}
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * soft info-box and badge colors below that the retrofit doesn't cover
 * for anyone.
 */
.dark .panel {
  @apply bg-gray-800 border-gray-700;
}
.dark .panel-title {
  @apply text-gray-100;
}
.dark .info-box {
  @apply bg-gray-900 border-gray-700;
}
.dark .info-label,
.dark .label {
  @apply text-gray-400;
}
.dark .info-value {
  @apply text-gray-100;
}
.dark .field {
  @apply bg-gray-700 border-gray-600 text-gray-100;
}

.dark .bg-yellow-100 {
  background-color: rgba(234, 179, 8, 0.2);
}
.dark .bg-orange-100 {
  background-color: rgba(249, 115, 22, 0.2);
}
.dark .bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2);
}
.dark .text-yellow-800 {
  color: theme('colors.yellow.300');
}
.dark .text-orange-800 {
  color: theme('colors.orange.300');
}
.dark .text-blue-800 {
  color: theme('colors.blue.300');
}
.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.35);
}
.dark .border-green-100,
.dark .border-green-200 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .border-yellow-200 {
  border-color: rgba(234, 179, 8, 0.35);
}
.dark .border-orange-100,
.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.35);
}
.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.35);
}
.dark .hover\:bg-indigo-100:hover {
  background-color: rgba(99, 102, 241, 0.25);
}
</style>
